import type { TFunction } from "i18next";

/**
 * Map a JS weekday index (0=Sun..6=Sat) to its `calendar.weekdays.*` i18n key.
 */
const WEEKDAY_KEYS = [
  "sun",
  "mon",
  "tue",
  "wed",
  "thu",
  "fri",
  "sat",
] as const;

/**
 * Join localized short weekday names. Weekdays are 0=Sun..6=Sat (JS
 * `Date.getDay()` convention). Names come from `calendar.weekdays.*`, the
 * conjunction from `common.and` ("&"/"och" equivalent — here "and"/"och").
 *
 * Examples (en): [1] -> "Mon"; [1,2] -> "Mon and Tue";
 * [1,2,3] -> "Mon, Tue and Wed".
 *
 * Out-of-range / duplicate indices are dropped; input order is preserved.
 */
export const formatWeekdays = (
  weekdays: number[],
  t: TFunction,
): string => {
  const seen = new Set<number>();
  const names = weekdays
    .filter((day) => {
      if (day < 0 || day > 6 || seen.has(day)) return false;
      seen.add(day);
      return true;
    })
    .map((day) => t(`calendar.weekdays.${WEEKDAY_KEYS[day]}`));

  if (names.length === 0) return "";
  if (names.length === 1) return names[0];

  const head = names.slice(0, -1).join(", ");
  const tail = names[names.length - 1];
  return `${head} ${t("common.and")} ${tail}`;
};
